import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { PayoutActions } from './payout.actions';
import {
  DocumentData,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { FIRESTORE } from 'src/app/common/firebase/firebase.tokens';
import { Action } from '@ngrx/store';
import { Payment } from '@enrollhere/classes';

/**
 * @deprecated NgRx effects with direct Firestore access are deprecated. Use API endpoints instead.
 */
@Injectable()
export class PayoutEffects {
  private actions$ = inject(Actions);
  /** @deprecated Direct Firestore access is deprecated. Use API endpoints instead. */
  private firestore = inject(FIRESTORE);

  private mapPayoutFromDoc(docSnapshot: {
    id: string;
    data: () => Record<string, unknown>;
  }) {
    return Payment.fromJSON({ ...docSnapshot.data(), id: docSnapshot.id });
  }

  private emitLoadPayoutsSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: {
      docs: Array<{ id: string; data: () => Record<string, unknown> }>;
    }
  ) {
    const payouts = snapshot.docs.map(docSnapshot =>
      this.mapPayoutFromDoc(docSnapshot)
    );
    subscriber.next(PayoutActions.loadPayoutsSuccess({ payouts }));
  }

  private emitLoadPayoutsFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(PayoutActions.loadPayoutsFailure({ error }));
  }

  private emitLoadPayoutSuccess(
    subscriber: { next: (action: Action) => void },
    snapshot: { id: string; data: () => Record<string, unknown> }
  ) {
    const payout = Payment.fromJSON({
      ...snapshot.data(),
      id: snapshot.id,
    });
    subscriber.next(PayoutActions.loadPayoutSuccess({ payout }));
  }

  private emitLoadPayoutFailure(
    subscriber: { next: (action: Action) => void },
    error: unknown
  ) {
    subscriber.next(PayoutActions.loadPayoutFailure({ error }));
  }

  private createAddPayoutSuccess(
    payout: Payment,
    agencyId: string,
    id: string
  ) {
    return PayoutActions.addPayoutSuccess({
      payout: { ...payout, id },
      agencyId,
    });
  }

  private createRemovePayoutSuccess(payoutId: string, agencyId: string) {
    return PayoutActions.removePayoutSuccess({ payoutId, agencyId });
  }

  private createUpdatePayoutSuccess(
    payoutId: string,
    agencyId: string,
    payout: Partial<Payment>
  ) {
    return PayoutActions.updatePayoutSuccess({
      payoutId,
      agencyId,
      payout,
    });
  }

  loadPayouts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PayoutActions.loadPayouts),
      switchMap(({ agencyId }) => {
        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            collection(this.firestore, 'agencies', agencyId, 'paymentsPayouts'),
            snapshot => {
              this.emitLoadPayoutsSuccess(
                subscriber,
                snapshot as {
                  docs: Array<{
                    id: string;
                    data: () => Record<string, unknown>;
                  }>;
                }
              );
            },
            error => {
              this.emitLoadPayoutsFailure(subscriber, error);
            }
          );

          // Provide a way of canceling and disposing the listener
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Payout API] Load Payouts Error', error })
          )
        );
      })
    );
  });

  loadPayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PayoutActions.loadPayout),
      switchMap(({ payoutId, agencyId }) => {
        return new Observable<Action>(subscriber => {
          const unsubscribe = onSnapshot(
            doc(
              this.firestore,
              'agencies',
              agencyId,
              'paymentsPayouts',
              payoutId
            ),
            snapshot => {
              this.emitLoadPayoutSuccess(
                subscriber,
                snapshot as {
                  id: string;
                  data: () => Record<string, unknown>;
                }
              );
            },
            error => {
              this.emitLoadPayoutFailure(subscriber, error);
            }
          );
          return unsubscribe;
        }).pipe(
          catchError(error =>
            of({ type: '[Payout API] Load Payout Error', error })
          )
        );
      })
    );
  });

  addPayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PayoutActions.addPayout),
      mergeMap(async ({ payout, agencyId }) => {
        try {
          const docRef = await addDoc(
            collection(this.firestore, 'agencies', agencyId, 'paymentsPayouts'),
            Payment.toJSON(payout)
          );
          return this.createAddPayoutSuccess(payout, agencyId, docRef.id); // return new payout with id
        } catch (error) {
          return PayoutActions.addPayoutFailure({ error });
        }
      })
    );
  });

  removePayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PayoutActions.removePayout),
      mergeMap(async ({ payoutId, agencyId }) => {
        try {
          await deleteDoc(
            doc(
              this.firestore,
              'agencies',
              agencyId,
              'paymentsPayouts',
              payoutId
            )
          );
          return this.createRemovePayoutSuccess(payoutId, agencyId); // return removed payout's id
        } catch (error) {
          return PayoutActions.removePayoutFailure({ error });
        }
      })
    );
  });

  updatePayout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PayoutActions.updatePayout),
      mergeMap(async ({ payoutId, agencyId, payout }) => {
        try {
          await setDoc(
            doc(
              this.firestore,
              'agencies',
              agencyId,
              'paymentsPayouts',
              payoutId
            ),
            Payment.toJSON(payout as Payment) as DocumentData,
            {
              merge: true,
            }
          );
          return this.createUpdatePayoutSuccess(payoutId, agencyId, payout); // return updated payout's id and changes
        } catch (error) {
          return PayoutActions.updatePayoutFailure({ error });
        }
      })
    );
  });
}
